<template>
  <nav v-if="isAuthenticated"
ref="navElement"
class="app-bottom-nav"
role="navigation"
:aria-label="$t('nav.mainNavigation')">
    <template v-for="item in navItems" :key="item.name">
      <AppNavigationItem
        v-if="item.name !== 'services'"
        :data-nav-name="item.name"
        :to="item.route"
        class="app-bottom-nav__item"
        :class="{ 'app-bottom-nav__item--active': isActive(item) }"
        :active="isActive(item)"
        :label="item.label"
        :icon="item.icon"
      />
      <RouterLink
        v-else
        :to="item.route"
        class="app-bottom-nav__item"
        :class="{ 'app-bottom-nav__item--active': isActive(item) }"
        :aria-current="isActive(item) ? 'page' : undefined"
        :data-nav-name="item.name"
        @click="handleServicesClick"
      >
        <i class="app-bottom-nav__icon zmdi zmdi-apps" aria-hidden="true" />
        <span class="app-bottom-nav__label" :class="{ 'interior-highlight': isActive(item) }">{{ item.label }}</span>
      </RouterLink>
    </template>

    <button
      v-if="isAuthenticated"
      class="app-bottom-nav__item"
      type="button"
      data-testid="bottom-nav-menu"
      :aria-label="$t('nav.openMenu')"
      @click="handleMenuClick"
    >
      <i class="app-bottom-nav__icon zmdi zmdi-menu" aria-hidden="true" />
      <span class="app-bottom-nav__label">{{ $t('nav.menu') }}</span>
    </button>
  </nav>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

import { useLayoutStore } from '@/store/layoutStore'
import { useProfileStore } from '@/modules/profile/store/profileStore'
import AppNavigationItem from './AppNavigationItem.vue'
import { FLOWS_DEMO_ROUTE } from '@/shared/config/navigation'

interface BottomNavItem {
  name: string
  label: string
  route: string
  /** Признак подсветки, когда префикса `route` недостаточно. */
  match?: RegExp
  icon: string
}

const { t } = useI18n()
const route = useRoute()
const layoutStore = useLayoutStore()
const profileStore = useProfileStore()

const { profile } = storeToRefs(profileStore)
const isAuthenticated = computed(() => !!profile.value)
const navElement = ref<HTMLElement | null>(null)

// Fixed page actions must sit above the actual nav, including wrapped labels
// and the device safe area, rather than assuming its minimum item height.
watch(navElement, (element, _previous, onCleanup) => {
  const layout = element?.closest<HTMLElement>('.layout')
  if (!element || !layout || typeof ResizeObserver === 'undefined') return
  const updateHeight = () => {
    layout.style.setProperty('--app-bottom-nav-height', `${element.getBoundingClientRect().height}px`)
  }
  const observer = new ResizeObserver(updateHeight)
  observer.observe(element)
  updateHeight()
  onCleanup(() => {
    observer.disconnect()
    layout.style.removeProperty('--app-bottom-nav-height')
  })
}, { flush: 'post' })

const navItems = computed<BottomNavItem[]>(() => [
  { name: 'home', label: t('nav.home'), route: '/dashboard', match: /^\/dashboard(?:\/|$)/, icon: 'zmdi-home' },
  {
    name: 'chains',
    label: t('nav.chains'),
    route: FLOWS_DEMO_ROUTE,
    match: /^\/chains(?:\/|$)/,
    icon: 'zmdi-gamepad',
  },
  { name: 'affiliate', label: t('nav.team'), route: '/affiliate', icon: 'zmdi-accounts' },
  { name: 'services', label: t('nav.services'), route: '/dashboard#more-ai', icon: 'zmdi-apps' },
])

function handleServicesClick(event: MouseEvent): void {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
  if (route.name !== 'dashboard-prestart' || route.hash !== '#more-ai') return

  const behavior = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
  document.getElementById('more-ai')?.scrollIntoView({ behavior, block: 'start' })
}

const isActive = (item: BottomNavItem) => {
  if (item.name === 'services') return route.name === 'dashboard-prestart' && route.hash === '#more-ai'
  if (item.name === 'home' && route.hash === '#more-ai') return false
  return item.match ? item.match.test(route.path) : route.path.startsWith(item.route)
}

const handleMenuClick = () => {
  layoutStore.toggleSidebar()
}
</script>

<style scoped lang="scss">
.app-bottom-nav {
  display: none;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: var(--hm-surface);
  border-top: 1px solid var(--hm-border);
  padding-bottom: env(safe-area-inset-bottom, 0);

  @media (max-width: 768px) {
    display: flex;
    align-items: stretch;
    justify-content: space-around;
  }

  &__item {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 12px 4px;
    min-height: 64px;
    border: none;
    background: transparent;
    color: var(--hm-text-muted);
    text-decoration: none;
    cursor: pointer;
    transition: color 0.2s;
    -webkit-tap-highlight-color: transparent;

    &:active {
      background: var(--ds-accent-soft-04);
    }

    &:focus-visible {
      outline: 2px solid var(--hm-accent);
      outline-offset: -3px;
    }
  }

  &__item--active {
    color: var(--hm-accent);
  }

  &__icon {
    font-size: var(--ds-text-size-h-md);
    line-height: 1;
  }

  &__label, :deep(.app-navigation-item__label) {
    font-size: var(--p-text-11);
    font-weight: var(--ds-weight-medium);
    letter-spacing: 0.01em;
    text-align: center;
  }
}
</style>
